import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Eye, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

export const ThemePreview = ({ uid, theme, layout, includeCalendars }) => {
  const [open, setOpen] = useState(true);
  const [zoom, setZoom] = useState(null);
  const { data, error, isFetching } = useQuery({
    queryKey: ["export-preview", uid, theme, includeCalendars, JSON.stringify(layout)],
    queryFn: () => api.post(`/universes/${uid}/export/preview-pages`, { categories: layout, include_calendars: includeCalendars, theme }).then((r) => r.data),
    enabled: open && !!layout && !!theme, staleTime: 60_000, placeholderData: (prev) => prev,
  });
  return (
    <div className="shrink-0" data-testid="export-theme-preview">
      <button type="button" onClick={() => setOpen(!open)} className="lf-label mb-1.5 flex items-center gap-1.5 hover:text-[#F3EBDD]" data-testid="export-preview-toggle">
        {open ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}{open ? "Hide preview" : "Show preview"}
      </button>
      {open && (
        <div className={`flex gap-2 transition-opacity ${isFetching ? "opacity-50" : ""}`}>
          {error && <p className="w-60 text-xs text-red-400" data-testid="export-preview-error">{errMsg(error)}</p>}
          {!data && !error && [0, 1, 2].map((i) => <div key={i} className="h-[120px] w-[85px] animate-pulse rounded-sm bg-[#1A202C]" />)}
          {data?.pages.map((p, i) => (
            <button key={p.label} type="button" onClick={() => setZoom(i)} className="group text-left" data-testid={`export-preview-page-${i}`}>
              <img src={p.image} alt={p.label} className="h-[120px] w-[85px] rounded-sm border border-[#2D3442] object-cover object-top shadow-md transition-transform duration-150 group-hover:-translate-y-0.5 group-hover:border-[#F59E0B]/60" />
              <div className="mt-1 w-[85px] text-[10px] leading-tight text-[#A8A090]">{p.label}</div>
            </button>
          ))}
        </div>
      )}
      {zoom != null && data?.pages[zoom] && (
        <Dialog open onOpenChange={(o) => !o && setZoom(null)}>
          <DialogContent className="max-w-2xl max-h-[94vh] overflow-y-auto lf-scroll" data-testid="export-preview-zoom">
            <DialogHeader><DialogTitle className="font-display text-2xl">{data.pages[zoom].label}</DialogTitle></DialogHeader>
            <img src={data.pages[zoom].image} alt={data.pages[zoom].label} className="w-full rounded-sm border border-[#2D3442]" />
            <div className="flex justify-center gap-2">
              {data.pages.map((p, i) => (
                <button key={p.label} type="button" onClick={() => setZoom(i)} data-testid={`export-preview-zoom-tab-${i}`}
                  className={`rounded-full border px-3 py-1 text-xs ${i === zoom ? "border-[#F59E0B] text-[#F59E0B]" : "border-[#2D3442] text-[#A8A090]"}`}>{p.label}</button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
